import { Injectable } from '@angular/core';
import { filter, from, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { AuthService } from '../auth.service';
import { AccessGuard } from '../access-guard';
import { environment } from '../../environments/environment';


function getTrustedAddinOrigins(): string[] {
  const origins = new Set<string>();
  
  if (environment.ADDIN_URL) {
    const origin = environment.ADDIN_URL.replace(/\/$/, '');
    origins.add(origin);
  }
  
  // Always include both local and production origins for security
  origins.add('https://localhost:3240');
  origins.add('https://word.app.silvernova.ai');
  
  return Array.from(origins);
}

@Injectable()
export class AddInAccessGuard extends AccessGuard {

  constructor(auth: AuthService) {
    super(auth);
  }

  private getWebDavRealmFromAddinPath(): string | null {
    const segs = window.location.pathname.split('/').filter(Boolean);
    const i = segs.indexOf('_addin');
    if (i < 0 || i + 1 >= segs.length) return null;
    return segs[i + 1];
  }

  private stashChatIdFromExchangePayload(payload: Record<string, string | undefined>): void {
    const chatId = payload['chat_id'];
    if (!chatId) return;
    const segs = window.location.pathname.split('/').filter(Boolean);
    const i = segs.indexOf('_addin');
    if (i < 0 || i + 2 >= segs.length) return;
    const fileId = segs[i + 2];
    if (!fileId || fileId === 'standalone' || fileId === 'auth-callback') return;
    sessionStorage.setItem(`addin.chatId.${fileId}`, chatId);
  }

  private stashChatIdFromDocumentToken(webDavOpaqueToken: string): Observable<void> {
    const realm = this.getWebDavRealmFromAddinPath();
    if (!realm || !webDavOpaqueToken) return of(undefined);
    return from(
      fetch(
        `${environment.apiUrl}webdav/${realm}/${webDavOpaqueToken}/exchange`
      ).then(async (response) => {
        if (response.status !== 201) return;
        const payload = (await response.json()) as Record<string, string | undefined>;
        this.stashChatIdFromExchangePayload(payload);
      })
    ).pipe(map(() => undefined));
  }

  private exchangeDocumentTokenForAccessToken(token: string): Observable<boolean> {
    const realm = this.getWebDavRealmFromAddinPath();
    if (!realm) {
      return of(false);
    }
    return from(fetch(`${environment.apiUrl}webdav/${realm}/${token}/exchange`)).pipe(
      switchMap((response) => {
        if (response.status !== 201) {
          // Redirect to the session expired page
          const url = new URL(window.location.href);
          url.pathname = '/session-expired.html';
          window.open(url.toString(), '_self');

          return of({
            access_token: undefined,
            refresh_token: undefined,
          });
        }
        return response.json();
      }),
      switchMap((response: Record<string, string | undefined>) => {
        if (response['access_token'] && response['refresh_token']) {
          sessionStorage.setItem('addin.word.accessToken', response['access_token']);
          sessionStorage.setItem('addin.word.refreshToken', response['refresh_token']);
        }
        this.stashChatIdFromExchangePayload(response);
        const accessToken = response['access_token'] ?? '';
        const refreshToken = response['refresh_token'] ?? '';
        return this.initKeycloakWithTokens(accessToken, refreshToken);
      }),
    )
  }

  // take(1): initKeycloak pushes ready$ false then true, so an open stream re-emits on
  // every later init. Init's refresh returning 400 (dead session) clears the token —
  // drop the stored copy so nothing replays it.
  private initKeycloakWithTokens(accessToken: string, refreshToken: string): Observable<boolean> {
    this.auth.initKeycloak(accessToken, refreshToken);
    return this.auth.ready$.pipe(
      filter(e => !!e),
      take(1),
      tap(() => {
        if (!this.auth.keycloak.token) {
          sessionStorage.removeItem('addin.word.accessToken');
          sessionStorage.removeItem('addin.word.refreshToken');
        }
      })
    );
  }

  private extractTokensFromHash(): { accessToken: string, refreshToken: string } | null {
    const hash = window.location.hash;
    if (!hash.includes('#auth=')) {
      return null;
    }

    try {
      const tokenData = hash.split('#auth=')[1].split('&')[0];
      const tokens = JSON.parse(atob(tokenData));
      if (tokens.access_token && tokens.refresh_token) {
        return {
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token
        };
      }
    } catch (e) {
      console.error('Error parsing tokens from URL hash:', e);
    }
    return null;
  }

  override canActivate(): Observable<boolean> {
    const currentPath = window.location.pathname;
    const webDavOpaqueToken = new URLSearchParams(window.location.search).get('token');

    const hashTokens = this.extractTokensFromHash();
    if (hashTokens) {
      sessionStorage.setItem('addin.word.accessToken', hashTokens.accessToken);
      sessionStorage.setItem('addin.word.refreshToken', hashTokens.refreshToken);
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      if (webDavOpaqueToken?.length) {
        return this.stashChatIdFromDocumentToken(webDavOpaqueToken).pipe(
          switchMap(() =>
            this.initKeycloakWithTokens(hashTokens.accessToken, hashTokens.refreshToken)
          )
        );
      }
      return this.initKeycloakWithTokens(hashTokens.accessToken, hashTokens.refreshToken);
    }

    const storedAccessToken = sessionStorage.getItem('addin.word.accessToken');
    const storedRefreshToken = sessionStorage.getItem('addin.word.refreshToken');
    let storedRefreshTokenValid = false;
    if (storedRefreshToken) {
      try {
        const refreshTokenParts = storedRefreshToken.split('.');
        const payload = JSON.parse(atob(refreshTokenParts[1]));
        const exp = payload.exp;
        const now = Math.floor(Date.now() / 1000);
        storedRefreshTokenValid = exp > now;
      } catch (e) {
        console.error('Error parsing refresh token:', e);
      }
    }

    // take(1) before the switchMap: initKeycloakWithTokens below re-emits ready$, which
    // would otherwise re-enter this projection and re-init with the same dead token.
    return this.auth.ready$.pipe(filter(e => !!e), take(1)).pipe(switchMap(() => {
      if (!this.auth.keycloak.token && storedAccessToken && storedRefreshToken && storedRefreshTokenValid) {
        if (webDavOpaqueToken?.length) {
          return this.stashChatIdFromDocumentToken(webDavOpaqueToken).pipe(
            switchMap(() =>
              this.initKeycloakWithTokens(storedAccessToken, storedRefreshToken)
            )
          );
        }
        return this.initKeycloakWithTokens(storedAccessToken, storedRefreshToken);
      }

      if (webDavOpaqueToken?.length && !this.auth.keycloak.token && !(storedAccessToken && storedRefreshToken && storedRefreshTokenValid)) {
        return this.exchangeDocumentTokenForAccessToken(webDavOpaqueToken);
      }
      
      if (this.auth.keycloak.token) {
        return super.canActivate();
      }
      
      if (currentPath.includes('/_addin/')) {
        if (window.parent !== window) {
          const trustedOrigins = getTrustedAddinOrigins();
          trustedOrigins.forEach(origin => {
            try {
              window.parent.postMessage('sessionExpired', origin);
            } catch {
              // Ignore errors for origins that don't match the parent
            }
          });
        }
        return of(false);
      }
      
      return super.canActivate();
    }));
  }
}
